import { useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  setOpenedLayerControlsId,
  clearOpenedLayerControlsId,
} from '@rsos/reduxDataStore/src/features/layerOpacity/layerOpacitySlice';
import { useAppDispatch } from '@rsos/utils/reduxHooks';
import { AppState } from '../../../types/AppState';
import { wait } from '../../../utils/wait';

export function usePopoverControls(id: string) {
  const dispatch = useAppDispatch();
  const openedLayerControlsId = useSelector(
    (state: AppState) => state.layerOpacity.openedLayerControlsId,
  );

  const isOpen = openedLayerControlsId === id;

  // In the close() function we wait for two seconds.
  // Because of this delay, it is possible that the user hovers back which would call the open() function.
  // To prevent the popover from closing in such a case, we use this ref as a flag.
  const shouldCancelClosing = useRef(false);

  // Store the current opened ID in a ref to avoid stale closure issues in the async close() function.
  // Without this ref, when switching quickly between popovers, the close() function would capture
  // a stale value of openedLayerControlsId in its closure (because async functions capture variables
  // at the time they're called, not when they finish executing). This could cause the newly opened popover
  // to be incorrectly cleared when the previous popover's close handler completes after the 2-second delay.
  // By using a ref that's updated on every render, we ensure close() always checks the latest value.
  const currentOpenedIdRef = useRef(openedLayerControlsId);
  currentOpenedIdRef.current = openedLayerControlsId;

  async function open() {
    shouldCancelClosing.current = true;
    dispatch(setOpenedLayerControlsId(id));
  }

  async function close() {
    shouldCancelClosing.current = false;
    await wait(2000);

    if (!shouldCancelClosing.current && currentOpenedIdRef.current === id) {
      dispatch(clearOpenedLayerControlsId());
    }
  }

  return {
    isOpen,
    open,
    close,
  };
}
